import { useQuery } from '@tanstack/react-query';
import type { Engagement } from '@ai-secure-twin/shared';
import { api } from '../../lib/api';
import { commandOf, type CommandEvent, type CommandStage } from '../../lib/engagement-command';

const time = (iso: string) =>
  new Date(iso).toLocaleString(undefined, {
    day: 'numeric',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  });

const STATE_LABEL: Record<CommandStage['state'], string> = {
  done: 'done',
  live: 'under way',
  waiting: 'waiting',
  blocked: 'needs attention',
};

/**
 * Engagement command (ADR 0010, UI direction D): this engagement's CTEM loop from scope to proof,
 * and what happened, newest first. Built from the records the panels below already read — the
 * same query keys, so it moves when they do and costs no extra request.
 */
export function EngagementCommand({ engagement }: { engagement: Engagement }) {
  const id = engagement.id;
  const scope = useQuery({ queryKey: ['scope', id], queryFn: () => api.listScopeVersions(id) });
  const approvals = useQuery({
    queryKey: ['approvalHistory', id],
    queryFn: () => api.listApprovals(id),
  });
  const scans = useQuery({ queryKey: ['scans', id], queryFn: () => api.listScans(id) });
  const findings = useQuery({ queryKey: ['findings', id], queryFn: () => api.listFindings(id) });
  const remediations = useQuery({
    queryKey: ['remediations', id],
    queryFn: () => api.listRemediations(id),
  });
  const agentRuns = useQuery({ queryKey: ['agentRuns', id], queryFn: () => api.listAgentRuns(id) });

  const { stages, events } = commandOf({
    scopeVersions: scope.data?.items ?? [],
    ...(engagement.currentScopeVersion !== undefined
      ? { currentScopeVersion: engagement.currentScopeVersion }
      : {}),
    approvals: approvals.data?.items ?? [],
    scans: scans.data?.items ?? [],
    findings: findings.data?.items ?? [],
    remediations: remediations.data?.items ?? [],
    agentRuns: agentRuns.data?.items ?? [],
  });

  return <CommandView stages={stages} events={events} />;
}

export function CommandView({
  stages,
  events,
}: {
  stages: readonly CommandStage[];
  events: readonly CommandEvent[];
}) {
  return (
    <section className="card panel cmd" aria-labelledby="cmd-title">
      <div className="xp-head">
        <h2 id="cmd-title" className="panel-title">
          Command
        </h2>
        <span className="muted cmd-sub">Scope → scan → triage → fix → deploy → proof</span>
      </div>
      <ol className="cmd-pipeline">
        {stages.map((s, i) => (
          <li
            key={s.key}
            className={`cmd-stage is-${s.state}`}
            aria-label={`${i + 1}. ${s.label}: ${s.value}, ${STATE_LABEL[s.state]}`}
          >
            <span className="cmd-stage-name">
              <span className="cmd-stage-n">{i + 1}</span>
              {s.label}
            </span>
            <span className="cmd-stage-value">{s.value}</span>
            <span className="cmd-stage-detail">{s.detail}</span>
            {s.progress !== undefined && s.state === 'live' ? (
              <span className="cmd-progress" aria-hidden="true">
                <i style={{ width: `${Math.round(s.progress * 100)}%` }} />
              </span>
            ) : null}
          </li>
        ))}
      </ol>
      <div className="cmd-activity">
        <h3 className="xp-eyebrow">Activity</h3>
        {events.length === 0 ? (
          <p className="muted">Nothing has happened yet. Define a scope to begin.</p>
        ) : (
          <ol className="cmd-timeline">
            {events.map((e, i) => (
              <li key={`${e.at}${e.title}${i}`} className={`cmd-event tone-${e.tone}`}>
                <time className="cmd-when" dateTime={e.at}>
                  {time(e.at)}
                </time>
                <span className="cmd-rail" aria-hidden="true">
                  <i />
                </span>
                <span className="cmd-what">
                  {e.title}
                  <small>{e.detail}</small>
                </span>
              </li>
            ))}
          </ol>
        )}
      </div>
    </section>
  );
}
